
'use client';

import { useState, useRef, FormEvent } from 'react';
import { LoaderCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { SectionWrapper } from '../section-wrapper';
import Link from 'next/link';
import { Label } from '../ui/label';

const initialSponsorshipMessage = 'Me gustaría recibir el dossier de patrocinio.';

function SponsorshipForm() {
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        
        const formData = new FormData(e.currentTarget);
        const body = Object.fromEntries(formData.entries());

        try {
            const res = await fetch('/api/sponsorship', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const data = await res.json();

            if (res.ok) {
                toast({
                    title: '¡Solicitud Enviada!',
                    description: data.message,
                });
                if (data.sheetError) {
                    toast({
                        title: 'Error al guardar en Google Sheet',
                        description: data.sheetError,
                        variant: 'destructive',
                    });
                }
                formRef.current?.reset();
                if(textAreaRef.current) {
                    textAreaRef.current.value = initialSponsorshipMessage;
                }
            } else {
                console.error('Error del servidor:', data);
                toast({
                    title: 'Error en la solicitud',
                    description: data.detail || data.message || 'Ocurrió un error al enviar la solicitud.',
                    variant: 'destructive',
                });
            }
        } catch (error) {
            console.error('Error al enviar solicitud de patrocinio:', error);
            toast({
                title: 'Error de Red',
                description: 'No se pudo conectar con el servidor. Inténtalo más tarde.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form 
            ref={formRef}
            onSubmit={handleSubmit}
            className="grid gap-4 text-left"
        >
            <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="name">Nombre completo *</Label>
                    <Input id="name" name="name" placeholder="Tu nombre y apellidos" required />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" name="email" type="email" placeholder="tu@email.com" required />
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="company">Empresa *</Label>
                    <Input id="company" name="company" placeholder="Nombre de tu empresa" required />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="jobTitle">Cargo *</Label>
                    <Input id="jobTitle" name="jobTitle" placeholder="Tu puesto de trabajo" required />
                </div>
            </div>
            <div className="space-y-2">
                <Label htmlFor="message">Mensaje *</Label>
                <Textarea 
                    id="message" 
                    name="message" 
                    defaultValue={initialSponsorshipMessage} 
                    ref={textAreaRef}
                    rows={5} 
                    required 
                />
            </div>

            {/* Campo Honeypot para engañar a los bots */}
            <div className="opacity-0 absolute h-0 w-0 z-[-1]">
              <label htmlFor="honeypot">No rellenes este campo si eres humano</label>
              <input type="text" id="honeypot" name="honeypot" tabIndex={-1} autoComplete="off" />
            </div>
            
            <div className="text-center text-xs text-muted-foreground mt-2">
                 <p>
                    Al pulsar "Solicitar Dossier", aceptas nuestra{' '}
                    <Link href="/politica-privacidad" className="underline hover:text-primary" target="_blank">
                        Política de Privacidad
                    </Link>.
                </p>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 font-bold text-lg">
              {loading ? <LoaderCircle className="animate-spin" /> : "SOLICITAR DOSSIER"}
            </Button>
        </form>
    );
};

export function SponsorshipFormSection() {
    return (
        <SectionWrapper id="sponsorship-form" className="bg-card text-card-foreground">
            <div className="container grid items-center justify-center gap-8 px-4 md:px-6">
                <div className="space-y-3 text-center">
                    <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl font-headline">SÚMATE AL <span className="text-primary">MOVIMIENTO</span></h1>
                    <p className="mx-auto max-w-[600px] text-card-foreground/80 md:text-xl/relaxed">
                        <span className="text-primary font-semibold">Sin Gluten Experience Day</span> es una oportunidad única para dar a conocer tu marca a miles de personas. Si estás interesado en patrocinar el evento, no dudes en ponerte en contacto con nosotros.
                    </p>
                </div>
                <div className="mx-auto w-full max-w-2xl space-y-6">
                    <div className="grid md:grid-cols-2 gap-6 text-left mb-12">
                        <div className="space-y-4">
                            <h2 className="text-xl font-bold font-headline text-primary">¿Por qué patrocinar?</h2>
                            <ul className="space-y-3 text-card-foreground/70">
                                <li>• <span className="text-secondary-foreground font-semibold">Toda la comunidad en un lugar:</span> Conecta de forma directa con tu público objetivo en un entorno de confianza.</li>
                                <li>• <span className="text-secondary-foreground font-semibold">Impacto nacional:</span> Una cobertura mediática masiva mediante nuestro programa de TV en directo.</li>
                                <li>• <span className="text-secondary-foreground font-semibold">Networking puro:</span> Crea alianzas estratégicas con otras marcas y profesionales líderes del sector.</li>
                            </ul>
                        </div>
                        <div className="space-y-4">
                             <h2 className="text-xl font-bold font-headline text-primary">Oportunidades únicas</h2>
                            <ul className="space-y-3 text-card-foreground/70">
                                <li>• <span className="text-secondary-foreground font-semibold">Zona de Expositores:</span> Un escaparate privilegiado para tus productos más novedosos.</li>
                                <li>• <span className="text-secondary-foreground font-semibold">Showcookings y Ponencias:</span> Espacios dedicados para demostrar el valor de tu marca.</li>
                                <li>• <span className="text-secondary-foreground font-semibold">Marketing Directo:</span> Prueba de productos y captación directa de feedback cualificado.</li>
                            </ul>
                        </div>
                    </div>
                    <div className="h-px bg-border/40 my-8"></div>
                    <SponsorshipForm />
                </div>
                <div className="mt-16 text-left space-y-8 max-w-4xl mx-auto border-t border-border/50 pt-12">
                    <h2 className="text-2xl font-bold font-headline">Impacto y Resultados Esperados</h2>
                    <div className="space-y-4">
                        <p className="text-card-foreground/80 md:text-lg">
                            Participar en el <span className="text-primary font-semibold">Sin Gluten Experience Day</span> no es solo poner un logo en un cartel. Es formar parte de una transformación en la forma en que la comunidad celíaca interactúa con las marcas. Esperamos una asistencia física de más de 2.000 personas y un alcance digital que supere los 50.000 impactos gracias a nuestra estrategia multicanal.
                        </p>
                        <p className="text-card-foreground/80 md:text-lg">
                            Nuestra propuesta de valor se basa en tres pilares fundamentales para tu marca:
                        </p>
                        <ul className="grid md:grid-cols-3 gap-6 mt-6">
                            <li className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                                <h3 className="font-bold text-primary mb-2">Visibilidad Cualificada</h3>
                                <p className="text-sm">No buscamos solo números, buscamos que tu producto llegue a las manos de quienes realmente lo necesitan y valoran su calidad.</p>
                            </li>
                            <li className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                                <h3 className="font-bold text-primary mb-2">Contenido de Calidad</h3>
                                <p className="text-sm">Generamos contenido profesional (vídeos, entrevistas, artículos) que tu marca podrá utilizar en sus propios canales después del evento.</p>
                            </li>
                            <li className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                                <h3 className="font-bold text-primary mb-2">Responsabilidad Social</h3>
                                <p className="text-sm">Posiciona tu empresa como un referente comprometido con la salud y el bienestar de un colectivo que crece cada año.</p>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
};
